import type { BillingInterval, Subscription } from '@/store/subscriptions/subscriptions.types';
import { PaymentStatus, SubscriptionTier } from '@/store/subscriptions/subscriptions.types';

/**
 * How a vessel's subscription reads in the list. Mirrors `subscription.display.ts` in the
 * subscriptions portal (frontend-subscriptions), so the same row says the same thing in
 * both apps.
 */

export const fmtDate = (at: number) =>
  new Date(at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

export const TIER_LABEL: Record<SubscriptionTier, string> = {
  [SubscriptionTier.HARBOR]: 'Harbor',
  [SubscriptionTier.NAVIGATE]: 'Navigate',
  [SubscriptionTier.COMMAND]: 'Command',
};

const INTERVAL_LABEL: Record<BillingInterval, string> = {
  monthly: 'Monthly',
  annually: 'Annually',
};

/**
 * Rows the server treats as dead — `DeadPaymentStatuses` in the People service's
 * `internal/pkg/database/subscription.go`. A row in one of these states carries a tier, an
 * interval and seat counts, but nothing was provisioned or is still provisioned behind it,
 * and the server excludes it when it looks for a service's live subscription.
 *
 * Typed to accept `null` so an unmapped `payment_status` falls outside the set and keeps
 * whatever tier it claims.
 */
const DEAD_STATUSES = new Set<PaymentStatus | null>([
  PaymentStatus.INCOMPLETE,
  PaymentStatus.INCOMPLETE_EXPIRED,
  PaymentStatus.CANCELED,
  // Not in the server's set — an `ended` row has no counterpart there, but it provisions
  // nothing either, so it reads the same way here.
  PaymentStatus.ENDED,
]);

/**
 * A row that still stands for something the server provisioned. A dead one does not: an
 * `incomplete` row has no Stripe subscription behind it yet, and a canceled one no longer
 * has. `harbor` *is* live — it is the free tier, which has no Stripe object at all.
 */
export const isUpdatable = (subscription: Subscription | null): subscription is Subscription =>
  !!subscription && !DEAD_STATUSES.has(subscription.paymentStatus);

/**
 * Whether the vessel actually holds a paid plan. No subscription row, a `harbor` row and
 * an unpaid checkout all read the same to the owner: the free plan.
 */
export const isPaidPlan = (subscription: Subscription | null): subscription is Subscription =>
  isUpdatable(subscription) && subscription.tier !== SubscriptionTier.HARBOR;

/**
 * A lifetime plan an admin granted: never billed, never expiring, and not something the
 * vessel can change, buy over or cancel.
 */
export const isManuallyGranted = (subscription: Subscription | null): boolean =>
  isPaidPlan(subscription) && subscription.manuallyGranted;

/**
 * Timestamps arrive as Go `time.Time`. An unset one marshals either to `''` — already
 * mapped to `null` — or to the year-1 zero value, which parses cleanly and would print as
 * "Jan 1, 1"; Stripe's own unset dates come back at the Unix epoch. Anything at or before
 * epoch is therefore not a date the user should be shown.
 */
const timestamp = (raw: string | null): number | null => {
  if (!raw) return null;
  const at = Date.parse(raw);
  return Number.isNaN(at) || at <= 0 ? null : at;
};

/**
 * What the end of the current period brings. A subscription set to cancel does not renew
 * — it ends, and nothing is charged then — and the free plan does neither, so `none`
 * carries no date to show at all.
 */
export type Renewal =
  | { kind: 'renews'; at: number }
  | { kind: 'ends'; at: number }
  | { kind: 'none'; at: null };

/**
 * `cancel_at_period_end` outranks the next payment: the subscription is still live but
 * nothing more will be charged, so the date the owner needs is when access stops, not a
 * renewal that will not happen.
 */
export const renewalOf = (subscription: Subscription | null): Renewal => {
  if (!isPaidPlan(subscription)) return { kind: 'none', at: null };

  if (subscription.cancelAtPeriodEnd) {
    const at = timestamp(subscription.cancelAt);
    return at ? { kind: 'ends', at } : { kind: 'none', at: null };
  }

  const at = timestamp(subscription.nextPaymentAt);
  return at ? { kind: 'renews', at } : { kind: 'none', at: null };
};

/**
 * When the owner's soonest trial runs out, or `null` when none of their vessels is on
 * one. A trialing subscription's next payment is the first charge *after* the trial, so
 * that date is also the day the trial ends — and a trial set to cancel ends at
 * `cancel_at` instead, which `renewalOf` already resolves.
 */
export const soonestTrialEnd = (subscriptions: (Subscription | null)[]): number | null => {
  const ends = subscriptions
    .filter((subscription) => subscription?.paymentStatus === PaymentStatus.TRIALING)
    .map((subscription) => renewalOf(subscription).at)
    .filter((at): at is number => at !== null);

  return ends.length ? Math.min(...ends) : null;
};

/** Maps to the `status_*` classes in SubscriptionSettingsPage.module.scss. */
export type StatusTone = 'free' | 'active' | 'trial' | 'pending' | 'past_due' | 'ended';

export type StatusView = { label: string; tone: StatusTone };

/**
 * The live payment states worth naming. Everything the portal folds into one chip is
 * folded the same way here — a paused or unpaid plan is the owner's to settle, exactly
 * like a past-due one. Dead states never reach this map: they read as the free plan.
 */
const STATUS_VIEW: Partial<Record<PaymentStatus, StatusView>> = {
  [PaymentStatus.TRIALING]: { label: 'Trial', tone: 'trial' },
  [PaymentStatus.PAST_DUE]: { label: 'Past due', tone: 'past_due' },
  [PaymentStatus.UNPAID]: { label: 'Unpaid', tone: 'past_due' },
  [PaymentStatus.PAUSED]: { label: 'Paused', tone: 'past_due' },
};

const ACTIVE: StatusView = { label: 'Active', tone: 'active' };

const LIFETIME: StatusView = { label: 'Lifetime', tone: 'active' };

/**
 * A scheduled cancellation outranks the payment status: the subscription is still live but
 * nothing more will be charged, so the date access stops is what the owner needs to see.
 */
export const statusView = (subscription: Subscription | null): StatusView => {
  if (!isPaidPlan(subscription)) return { label: 'Free', tone: 'free' };
  if (subscription.manuallyGranted) return LIFETIME;

  const renewal = renewalOf(subscription);
  if (renewal.kind === 'ends') return { label: `Ends ${fmtDate(renewal.at)}`, tone: 'ended' };

  if (!subscription.paymentStatus) {
    // An undocumented payment_status — show it rather than guess at a label.
    return { label: subscription.paymentStatusRaw || 'Unknown', tone: 'pending' };
  }
  return STATUS_VIEW[subscription.paymentStatus] ?? ACTIVE;
};

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

/**
 * `seats` is the billable seat count and `crew_seats` a separate add-on — named
 * Management and Crew here, the same two labels the portal's checkout form steps. Both are
 * always stated, including a zero, so the split is readable without opening the vessel.
 * The API does not report how many are occupied, so this is the allocation, not usage.
 */
const seatsSummary = (subscription: Subscription): string =>
  [plural(subscription.seats, 'management seat'), plural(subscription.crewSeats, 'crew seat')].join(
    ' · ',
  );

/**
 * The plan's name alone — `Harbor` for every shape of the free plan, otherwise the tier.
 * This is the part that fits in a heading; `planSummary` states the same name with the
 * interval and seat counts after it.
 */
export const planName = (subscription: Subscription | null): string => {
  if (!isPaidPlan(subscription)) return TIER_LABEL[SubscriptionTier.HARBOR];
  return subscription.tier ? TIER_LABEL[subscription.tier] : 'Unknown plan';
};

export const planSummary = (subscription: Subscription | null): string => {
  // The free plan has no seat counts to state — the tier name is the whole line.
  if (!isPaidPlan(subscription)) return planName(subscription);

  // A granted plan has no billing interval; where one would go, it is named for what it is.
  const interval = subscription.manuallyGranted
    ? 'Lifetime'
    : subscription.interval
      ? INTERVAL_LABEL[subscription.interval]
      : null;
  return [planName(subscription), interval, seatsSummary(subscription)].filter(Boolean).join(' · ');
};
